import { useState, type ReactNode } from 'react';
import type { Directory } from '@stitch/pbx-adapter';
import { api, type PersonPresence } from '../api/client';
import { accessTo, useAuth } from '../lib/auth';
import { PresenceBadge, presenceText } from './Presence';
import { Button, Dialog, Notice } from './ui';

/**
 * Who may turn off someone else's do not disturb: the company's admins, and Stitch staff viewing
 * the company. Never end users (the server refuses them too).
 */
export function useCanTurnOffDnd(domain: string): boolean {
  const { state } = useAuth();
  const access = accessTo(state, domain);
  return access === 'admin' || access === 'staff';
}

/**
 * "Turn off do not disturb" for someone else: a confirm, then one change set applied at once
 * (read back, undoable from Change history, every open Stitch hears it, recorded under the admin's
 * name; the person gets a short notice). `trigger` is what to click: the status itself, or a
 * button. Renders nothing for people who may not do it.
 */
export function TurnOffDnd({
  domain,
  extension,
  name,
  trigger,
  label,
  onDone
}: {
  domain: string;
  extension: string;
  /** "Rania Alvarado": the confirm uses the first name. */
  name: string;
  /** What to show (it becomes the button's content). */
  trigger: ReactNode;
  /** Words for screen readers, when the trigger's own aren't enough. */
  label?: string;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const first = name.trim().split(/\s+/)[0] || `extension ${extension}`;
  const close = () => {
    if (busy) return;
    setOpen(false);
    setProblem('');
  };
  const run = async () => {
    setBusy(true);
    setProblem('');
    try {
      const r = await api.myDnd(domain, false, extension);
      if (r.status === 'applied' || r.status === 'unchanged') {
        setOpen(false);
        onDone?.();
      } else {
        setProblem(r.message ?? 'That didn’t work. Nothing was changed. Try again.');
      }
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button
        type="button"
        className="dnd-off"
        title={`Turn off ${first}’s do not disturb`}
        aria-label={label ?? `Turn off ${name}’s do not disturb`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {trigger}
      </button>
      <Dialog
        open={open}
        onClose={close}
        title={`Turn off ${first}’s do not disturb?`}
        subtitle="Their phones and apps will ring again."
        foot={
          <>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void run()} disabled={busy}>
              {busy ? 'Turning it off…' : 'Turn it off'}
            </Button>
          </>
        }
      >
        <p className="muted">It’s recorded under your name, and you can undo it from Change history. If {first} uses Stitch, they get a short notice.</p>
        {problem && <Notice tone="crit">{problem}</Notice>}
      </Dialog>
    </>
  );
}

/**
 * Someone's status (on a call, available...). For an admin, "Do not disturb" is also the way to
 * turn it off: "● Do not disturb · Turn off".
 */
export function PresenceWithDndOff({
  domain,
  extension,
  name,
  p,
  dir,
  canTurnOff,
  onDone
}: {
  domain: string;
  extension: string;
  name: string;
  p: PersonPresence | undefined;
  dir: Directory | undefined;
  /** This viewer may (useCanTurnOffDnd), and the company can be changed here. */
  canTurnOff: boolean;
  onDone?: () => void;
}) {
  if (!p || !canTurnOff || presenceText(p, dir).tone !== 'dnd') return <PresenceBadge p={p} dir={dir} />;
  return (
    <TurnOffDnd
      domain={domain}
      extension={extension}
      name={name}
      {...(onDone ? { onDone } : {})}
      trigger={
        <>
          <PresenceBadge p={p} dir={dir} />
          <span className="dnd-off-action" aria-hidden="true">
            · Turn off
          </span>
        </>
      }
    />
  );
}
